import { supabase } from "@/integrations/supabase/client";

/**
 * Locations are soft-deleted: `archived = true` hides a row from every
 * picker, filter, list and address-matching path, while keeping it in the
 * database so historical orders/legs that reference it still resolve.
 *
 * Use `ACTIVE_LOCATION_FILTER` as the single source of truth for the column
 * name so a future rename is a one-line change.
 */
export const ACTIVE_LOCATION_FILTER = { archived: false } as const;

const ACTIVE_ORDER_STATUSES = [
  "booked",
  "confirmed",
  "in_transit",
  "tender_received",
] as const;

/**
 * Counts active (non-delivered, non-canceled) orders that still point at any
 * of the given locations, either directly or through one of their legs.
 * Used to warn — never block — before archiving.
 */
export async function countActiveOrdersForLocations(
  locationIds: string[],
): Promise<number> {
  if (locationIds.length === 0) return 0;

  const orderIds = new Set<string>();

  // Two exact-match `.in()` queries per table instead of a composed `.or()`
  // string — never interpolate values into PostgREST filter syntax.
  const orderQueries = (["ship_from_location_id", "ship_to_location_id"] as const).map(
    (column) =>
      supabase
        .from("orders")
        .select("id")
        .in("status", [...ACTIVE_ORDER_STATUSES])
        .in(column, locationIds),
  );
  for (const { data, error } of await Promise.all(orderQueries)) {
    if (error) throw error;
    (data ?? []).forEach((row) => orderIds.add(row.id as string));
  }

  const legQueries = (["ship_from_location_id", "ship_to_location_id"] as const).map(
    (column) => supabase.from("legs").select("order_id").in(column, locationIds),
  );
  const legOrderIdSet = new Set<string>();
  for (const { data, error } of await Promise.all(legQueries)) {
    if (error) throw error;
    (data ?? []).forEach((row) => {
      const id = row.order_id as string | null;
      if (id) legOrderIdSet.add(id);
    });
  }
  const legOrderIds = Array.from(legOrderIdSet);

  if (legOrderIds.length > 0) {
    const { data: parents, error: parentErr } = await supabase
      .from("orders")
      .select("id")
      .in("id", legOrderIds)
      .in("status", [...ACTIVE_ORDER_STATUSES]);
    if (parentErr) throw parentErr;
    (parents ?? []).forEach((row) => orderIds.add(row.id as string));
  }

  return orderIds.size;
}

/** Marks the given locations archived. Never hard-deletes. */
export async function archiveLocations(locationIds: string[]): Promise<number> {
  if (locationIds.length === 0) return 0;
  const { error } = await supabase
    .from("locations")
    .update({ archived: true, archived_at: new Date().toISOString() })
    .in("id", locationIds);
  if (error) throw error;
  return locationIds.length;
}
